/**
 * Podpola wersji specyficzne dla zwykłego "Mercedes Benz Tourist Line"
 * (NIE HighDeck) - patrz sekcja 6 KNOWLEDGE_BASE.md. Ta zabudowa nie składa
 * {{version}} z tonażu/rozstawu osi jak Iveco / MB HighDeck, tylko z trzech
 * własnych kodów:
 *
 *  1. `versionPassengersCode` - kod ilości miejsc (2 cyfry), wyliczany z
 *     wariantu miejsc zamówienia (`order.places.variants[0]`) oraz opcji
 *     "przedłużenie tylne" (`order.options`),
 *  2. `versionMassCode` - pojedyncza litera wybierana ręcznie: C (MASA 5500)
 *     albo F (MASA 6100),
 *  3. `versionWheelchairCode` - P (bez wózków inwalidzkich) albo W (z
 *     wózkami), wyliczany z `wheelChairsCount` wariantu miejsc.
 *
 * Wynikowa wersja to sklejenie: `passengersCode + massCode + wheelchairCode`.
 *
 * @module mercedes-tourist-line-version
 */

import { isOrderOptionActiveById, type OrderOption } from "./order-options";

/**
 * `_id` opcji zamówienia "przedłużenie tylne" (wydłużenie tyłu pojazdu) -
 * pojedyncza opcja rozpoznawana wprost po id, aktywna gdy `checked` LUB
 * `required` (patrz `isOrderOptionActiveById`, analogicznie do haka
 * holowniczego w tow-hook.ts).
 */
export const REAR_EXTENSION_OPTION_ID = "5fa902ead4a903a4aa2f77c3";

/**
 * Fragment `order.places.variants[0]` w zakresie potrzebnym do wyliczenia
 * kodów wersji Mercedes Benz Tourist Line.
 */
export interface PlacesVariantForVersion {
  passengersCount?: number | null;
  driversCount?: number | null;
  wheelChairsCount?: number | null;
}

/**
 * Kod ilości miejsc ({{versionPassengersCode}}) dla Mercedes Benz Tourist
 * Line. Reguły (w kolejności priorytetu; wszystkie zakładają 1 kierowcę -
 * `pilotsCount` NIE wpływa na kod):
 *
 *  1. przedłużenie tylne (opcja `REAR_EXTENSION_OPTION_ID` aktywna):
 *     22 pasażerów -> "19", 19 pasażerów -> "18",
 *  2. wózki inwalidzkie na pokładzie (`wheelChairsCount` > 0) -> "37",
 *  3. `wheelChairsCount` 0/brak: 19 pasażerów -> "08", 16 pasażerów -> "10".
 *
 * Brak dopasowania (w tym przedłużenie tylne z inną liczbą pasażerów) -> ""
 * - pole zostaje puste do ręcznego uzupełnienia (jest wymagane przy zapisie,
 * patrz `getMissingVersionSubfieldLabels`).
 *
 * @param variant - `order.places.variants[0]`
 * @param orderOptions - `order.options`
 */
export function getMercedesTouristLinePassengersCode(
  variant: PlacesVariantForVersion | undefined | null,
  orderOptions: OrderOption[] | undefined | null,
): string {
  const passengersCount = variant?.passengersCount ?? 0;
  const driversCount = variant?.driversCount ?? 0;

  // Przedłużenie tylne ma własny zestaw kodów, zależny od liczby pasażerów
  // (przy 1 kierowcy). Poza znanymi kombinacjami -> "" (wybór ręczny) - nie
  // spadamy do kodów dla wersji bez przedłużenia, bo byłyby błędne.
  if (isOrderOptionActiveById(orderOptions, REAR_EXTENSION_OPTION_ID)) {
    if (driversCount === 1) {
      if (passengersCount === 22) return "19";
      if (passengersCount === 19) return "18";
    }
    return "";
  }

  const wheelChairsCount = variant?.wheelChairsCount ?? 0;
  if (wheelChairsCount > 0) {
    return "37";
  }

  if (driversCount === 1) {
    if (passengersCount === 19) return "08";
    if (passengersCount === 16) return "10";
  }

  return "";
}

/**
 * Kod masy ({{versionMassCode}}) dla Mercedes Benz Tourist Line - "C" (MASA
 * 5500) albo "F" (MASA 6100), wyznaczany z dopuszczalnej masy całkowitej
 * pojazdu ze zlecenia (`order.vehicleWeight.weight`, w kg). Próg 6000 kg
 * rozdziela dwa warianty homologacyjne Sprintera (5500 / 6100). Brak/0 masy
 * -> "" (do ręcznego wyboru). Pole pozostaje nadpisywalne.
 *
 * @param vehicleWeightKg - `order.vehicleWeight.weight` (kg)
 */
export function getMercedesTouristLineMassCode(
  vehicleWeightKg: number | string | undefined | null,
): "" | "C" | "F" {
  const weight = Number(vehicleWeightKg);
  if (!Number.isFinite(weight) || weight <= 0) {
    return "";
  }
  return weight >= 6000 ? "F" : "C";
}

/**
 * Kod obecności wózków inwalidzkich ({{versionWheelchairCode}}) dla Mercedes
 * Benz Tourist Line - "W" gdy w wariancie miejsc jest co najmniej jeden
 * wózek, "P" w przeciwnym razie (0 albo brak pola).
 *
 * @param variant - `order.places.variants[0]`
 */
export function getMercedesTouristLineWheelchairCode(
  variant: PlacesVariantForVersion | undefined | null,
): "P" | "W" {
  return (variant?.wheelChairsCount ?? 0) > 0 ? "W" : "P";
}
